import { setToken } from '@/api/http-service/PrepareAPIService'

const GUEST_AUTH_TOKEN_KEY = 'prepareGuestAuthToken'
// Assuming prepare-api runs on 4001 locally. Adjust if needed.
const AUTH_API_URL =
  import.meta.env.VITE_PREPARE_API_URL || 'http://localhost:4001'

let currentToken: string | null = null
let tokenPromise: Promise<string> | null = null

/**
 * Fetches a new guest token from the API.
 */
async function fetchNewGuestToken(): Promise<string> {
  try {
    const response = await fetch(`${AUTH_API_URL}/auth/guest`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({})) // Attempt to parse error
      throw new Error(
        `Failed to fetch guest token: ${response.status} ${response.statusText} - ${JSON.stringify(errorData)}`
      )
    }

    const data = await response.json()
    if (!data || !data.token) {
      throw new Error('Invalid response format from auth/guest endpoint')
    }

    return data.token
  } catch (error) {
    console.error('Error fetching guest auth token:', error)
    // Depending on requirements, you might want to retry or throw
    // to prevent proceeding without auth.
    throw error // Re-throw for now
  }
}

/**
 * Retrieves the guest authentication token.
 * Checks localStorage first, then fetches a new token if needed.
 * Caches the token in memory and localStorage.
 * Manages concurrent requests for the token.
 */
export async function getAuthToken(): Promise<string> {
  console.warn('getAuthToken called - this should not happen with Better Auth!')
  console.trace('Stack trace for getAuthToken call')

  // 1. Check memory cache
  if (currentToken) {
    setToken(currentToken)
    return currentToken
  }

  // 2. Check localStorage
  if (typeof window !== 'undefined') {
    // Ensure localStorage is available
    const storedToken = localStorage.getItem(GUEST_AUTH_TOKEN_KEY)
    if (storedToken) {
      currentToken = storedToken
      // TODO: Add token validation/expiration check here if needed in the future
      setToken(currentToken)
      return currentToken
    }
  }

  console.error(
    'Guest auth should be disabled - throwing error instead of creating guest user'
  )
  throw new Error(
    'Guest authentication is disabled. Please use Email/social instead.'
  )
}

/**
 * Alias for getAuthToken for compatibility
 */
export const getGuestAuthToken = getAuthToken

/**
 * Clears the stored guest authentication token from memory and localStorage.
 */
export function clearAuthToken(): void {
  currentToken = null
  tokenPromise = null
  if (typeof window !== 'undefined') {
    localStorage.removeItem(GUEST_AUTH_TOKEN_KEY)
  }
  setToken(null)
}
